/**
 * Простые графики на SVG без библиотек: столбцы по дням (один или два ряда, стопкой) и горизонтальные полосы.
 * Цвета — роли --series-1/--series-2 из темы (проверены на различимость, в т.ч. при дальтонизме).
 * Подсказка при наведении/фокусе, легенда для двух рядов, переключение на таблицу.
 */
import { useId, useMemo, useState } from 'react';

export interface Series {
  key: string;
  label: string;
}

const DAY_FMT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
export const fmtDay = (iso: string) => DAY_FMT.format(new Date(`${iso}T00:00:00Z`));
export const fmtNum = (n: number) => n.toLocaleString('ru-RU');

/** «Красивый» верх оси: 1, 2, 5 × 10ⁿ. */
function niceMax(v: number): number {
  if (v <= 4) return 4;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

export function ColumnChart({
  rows,
  series,
  height = 180,
  valueFmt = fmtNum,
  ariaLabel,
}: {
  rows: (Record<string, number | string> & { day: string })[];
  series: Series[];
  height?: number;
  valueFmt?: (n: number) => string;
  ariaLabel: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const id = useId();
  const W = 640;
  const H = height;
  const padL = 34;
  const padB = 22;
  const padT = 8;
  const totals = rows.map((r) => series.reduce((s, x) => s + Number(r[x.key] ?? 0), 0));
  const max = niceMax(Math.max(0, ...totals));
  const plotW = W - padL - 4;
  const plotH = H - padB - padT;
  const band = plotW / Math.max(rows.length, 1);
  const barW = Math.max(2, Math.min(24, band * 0.62));
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  const ticks = [0, max / 2, max];
  const labelEvery = Math.ceil(rows.length / 7);
  const sum = totals.reduce((a, b) => a + b, 0);

  return (
    <div className="chart">
      {series.length > 1 ? (
        <div className="legend">
          {series.map((s, i) => (
            <span key={s.key}>
              <i style={{ background: `var(--series-${i + 1})` }} />
              {s.label}: {valueFmt(rows.reduce((a, r) => a + Number(r[s.key] ?? 0), 0))}
            </span>
          ))}
        </div>
      ) : null}
      {asTable ? (
        <table className="mini-table">
          <thead>
            <tr>
              <th>День</th>
              {series.map((s) => <th key={s.key} className="num-col">{s.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.day}>
                <td>{fmtDay(r.day)}</td>
                {series.map((s) => <td key={s.key} className="num-col">{valueFmt(Number(r[s.key] ?? 0))}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${id}-t`} onMouseLeave={() => setHover(null)}>
          <title id={`${id}-t`}>{`${ariaLabel}: всего ${valueFmt(sum)}`}</title>
          {ticks.map((t) => (
            <g key={t}>
              <line className="gridline" x1={padL} x2={W} y1={y(t)} y2={y(t)} />
              <text className="axis-label" x={padL - 6} y={y(t) + 4} textAnchor="end">{valueFmt(Math.round(t))}</text>
            </g>
          ))}
          {rows.map((r, i) => {
            const cx = padL + band * i + band / 2;
            let acc = 0;
            return (
              <g key={r.day}>
                <rect
                  className="hit"
                  x={padL + band * i}
                  y={padT}
                  width={band}
                  height={plotH}
                  tabIndex={0}
                  aria-label={`${fmtDay(r.day)}: ${series.map((s) => `${s.label} ${valueFmt(Number(r[s.key] ?? 0))}`).join(', ')}`}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
                <rect className={`hover-band ${hover === i ? 'on' : ''}`} x={padL + band * i} y={padT} width={band} height={plotH} />
                {series.map((s, si) => {
                  const v = Number(r[s.key] ?? 0);
                  if (v <= 0) return null;
                  const top = y(acc + v);
                  const bottom = y(acc);
                  acc += v;
                  const h = Math.max(1, bottom - top - (si > 0 ? 2 : 0)); // 2px зазор между сегментами
                  const isTop = series.slice(si + 1).every((x) => Number(r[x.key] ?? 0) <= 0);
                  const rad = isTop ? Math.min(4, barW / 2, h) : 0;
                  return (
                    <path
                      key={s.key}
                      className={`bar-${si + 1}`}
                      pointerEvents="none"
                      d={`M${cx - barW / 2},${bottom - (si > 0 ? 2 : 0)} V${top + rad} Q${cx - barW / 2},${top} ${cx - barW / 2 + rad},${top} H${cx + barW / 2 - rad} Q${cx + barW / 2},${top} ${cx + barW / 2},${top + rad} V${bottom - (si > 0 ? 2 : 0)} Z`}
                      data-h={h}
                    />
                  );
                })}
                {i % labelEvery === 0 || i === rows.length - 1 ? (
                  <text className="axis-label" x={cx} y={H - 6} textAnchor="middle">{fmtDay(r.day)}</text>
                ) : null}
              </g>
            );
          })}
        </svg>
      )}
      {hover !== null && !asTable && rows[hover] ? (
        <div className="chart-tip" style={{ left: `${((padL + band * hover + band / 2) / W) * 100}%`, top: `${(y(totals[hover]!) / H) * 100}%` }} role="status">
          <b>{fmtDay(rows[hover]!.day)}</b>
          {series.map((s, i) => (
            <div key={s.key}>
              {series.length > 1 ? <i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 6, background: `var(--series-${i + 1})` }} /> : null}
              {s.label}: <b style={{ display: 'inline' }}>{valueFmt(Number(rows[hover]![s.key] ?? 0))}</b>
            </div>
          ))}
        </div>
      ) : null}
      <button type="button" className="link table-toggle" onClick={() => setAsTable((v) => !v)}>
        {asTable ? 'Показать график' : 'Показать таблицей'}
      </button>
    </div>
  );
}

/** Горизонтальные полосы (например, объявления по категориям). Значение подписано у каждой полосы. */
export function HBars({ items, valueFmt = fmtNum }: { items: { label: string; value: number; hint?: string }[]; valueFmt?: (n: number) => string }) {
  const max = useMemo(() => Math.max(1, ...items.map((i) => i.value)), [items]);
  return (
    <div className="hbars" role="list">
      {items.map((i) => (
        <div key={i.label} className="hbar" role="listitem" title={i.hint}>
          <span>{i.label}</span>
          <div className="track" aria-hidden="true">
            <div className="fill" style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
          <span className="num">{valueFmt(i.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Изменение к прошлому периоду: «+12 %», «−5 %», «новое». Цвет — текстовые токены, не цвет ряда. */
export function Delta({ cur, prev, invert = false }: { cur: number; prev: number; invert?: boolean }) {
  if (!prev && !cur) return <span className="tile-delta">без изменений</span>;
  if (!prev) return <span className="tile-delta up">новое за период</span>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const good = invert ? pct < 0 : pct > 0;
  const cls = pct === 0 ? '' : good ? 'up' : 'down';
  return (
    <span className={`tile-delta ${cls}`}>
      {pct > 0 ? '▲ +' : pct < 0 ? '▼ −' : ''}
      {Math.abs(pct)} % к прошлому периоду
    </span>
  );
}
